/**
 * Type and helper for message objects (e.g. defineMessages result).
 * Use with react-intl or any keyed message map to get typed keys and an enum-like object.
 */

/** Type of a single key of a messages object. Use for variables and function params. */
export type MessageKey<T> = keyof T;

/**
 * Enum-like object type: same keys as the messages object, each value is the key string.
 * Use with createMessageEnum() to build the runtime object.
 */
type MessageEnum<T> = { [K in keyof T]: K };

/**
 * Builds an enum-like object from a messages object's keys.
 * Each property is the key string (e.g. AuthErrors.ERR_USER_NOT_REGISTERED === 'ERR_USER_NOT_REGISTERED').
 * Stays in sync when keys are added or removed from the messages object.
 *
 * @param messages - Object whose keys are used (e.g. result of defineMessages()).
 * @returns Object with same keys and string values equal to the key names.
 */
export function createMessageEnum<T extends Record<string, unknown>>(messages: T): MessageEnum<T> {
  return Object.fromEntries(
    (Object.keys(messages) as (keyof T)[]).map((key) => [key, key]),
  ) as MessageEnum<T>;
}
